import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { TID } from "../constants/testIds";
import { Plus, Search, Building2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { canManage } from "../lib/utils-app";
import RegistryLookup from "../components/RegistryLookup";

const FIELDS = [
  ["ragione_sociale", "Ragione sociale *", true],
  ["partita_iva", "Partita IVA"],
  ["codice_fiscale", "Codice fiscale"],
  ["codice_ateco", "Codice ATECO"],
  ["sede_legale", "Sede legale"],
  ["sede_operativa", "Sede operativa"],
  ["pec", "PEC"],
  ["email", "Email"],
  ["telefono", "Telefono"],
  ["datore_lavoro", "Datore di Lavoro / Legale Rappresentante"],
  ["attivita", "Attività svolta"],
];

export default function Companies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/companies", { params: { q } });
    setItems(data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", form);
      toast.success("Azienda creata");
      setOpen(false); setForm({}); load();
    } catch (err) {
      toast.error("Errore creazione");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Anagrafica</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Aziende clienti</h1>
          <p className="text-slate-500 mt-1">Gestione anagrafica e documentazione aziendale.</p>
        </div>
        {canManage(user) && <button onClick={() => setOpen(true)} data-testid={TID.addCompany}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-medium">
          <Plus size={18} /> Nuova azienda
        </button>}
      </header>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input placeholder="Cerca per ragione sociale, P.IVA, CF, ATECO…" value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="companies-search"
          className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg bg-white" />
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg py-16 text-center text-slate-500">
          <Building2 size={32} className="mx-auto mb-2 text-slate-400" />
          Nessuna azienda censita. Clicca "Nuova azienda" per iniziare.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(c => (
            <Link key={c.id} to={`/aziende/${c.id}`}
              data-testid={`company-card-${c.id}`}
              className="card-lift block bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-md bg-blue-50 text-blue-800 flex items-center justify-center shrink-0">
                  <Building2 size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-slate-900 truncate">{c.ragione_sociale}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">
                    {c.partita_iva && `P.IVA ${c.partita_iva}`}
                    {c.codice_ateco && ` · ATECO ${c.codice_ateco}`}
                  </div>
                  <div className="text-sm text-slate-600 mt-2 line-clamp-2">{c.sede_legale || "—"}</div>
                  {c.studio_name && <div className="mt-2 inline-block text-[11px] bg-blue-50 text-blue-800 px-2 py-0.5 rounded-full">Studio: {c.studio_name}</div>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="font-display font-semibold text-lg">Nuova azienda</h3>
              <button onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <RegistryLookup value={form.partita_iva || form.codice_fiscale} onFill={(fields) => setForm(f => ({ ...f, ...fields }))} />
              {FIELDS.map(([k, label, required]) => (
                <div key={k} className={k === "ragione_sociale" || k === "attivita" ? "md:col-span-2" : ""}>
                  <label className="text-sm font-medium text-slate-700">{label}</label>
                  <input required={required} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`company-${k}`}
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
              ))}
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-slate-600">Annulla</button>
                <button type="submit" data-testid={TID.companySubmit}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg">Crea azienda</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
